import React, { useState, useEffect } from 'react';
import {
    Card, Form, Input, InputNumber, Select, DatePicker, Button,
    Table, Typography, Space, message, Row, Col, Tag, Alert,
    Divider, Statistic, Spin, Grid
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined, SaveOutlined, TruckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';

const { Title, Text } = Typography;
const { Option } = Select;

const fmt = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PalayHaulingForm = () => {
    const screens = { md: window.innerWidth >= 768 };
    const isMobile = !screens.md;
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [availableLots, setAvailableLots] = useState([]);
    const [selectedLots, setSelectedLots] = useState([]);
    const [lotsLoading, setLotsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [branchId, setBranchId] = useState(null);

    useEffect(() => {
        Promise.all([
            client.get('/master/branches').catch(() => ({ data: [] })),
            client.get('/master/locations').catch(() => ({ data: [] })),
            client.get('/master/vehicles').catch(() => ({ data: [] })),
        ]).then(([b, l, v]) => {
            setBranches(b.data);
            setLocations(l.data);
            setVehicles(v.data);
        }).catch(() => message.error('Failed to load master data'));
    }, []);

    useEffect(() => {
        if (branchId) fetchAvailableLots(branchId);
        else setAvailableLots([]);
        setSelectedLots([]);
    }, [branchId]);

    const fetchAvailableLots = async (bId) => {
        setLotsLoading(true);
        try {
            const { data } = await client.get('/transactions/palay-hauling/available-lots', { params: { branch_id: bId } });
            setAvailableLots(data);
        } catch {
            message.error('Failed to load dried lots');
        } finally {
            setLotsLoading(false);
        }
    };

    const addLot = (lot) => {
        if (selectedLots.find(l => l.palay_lot_id === lot.id)) {
            message.warning('Lot already added');
            return;
        }
        setSelectedLots(prev => [...prev, {
            palay_lot_id:     lot.id,
            lot_number:       lot.lot_number,
            supplier:         lot.purchase?.partner?.name ?? '—',
            purchase_ticket:  lot.purchase?.ticket_number ?? '—',
            location:         lot.location?.name ?? '—',
            drying_number:    lot.drying_order?.drying_number ?? '—',
            quantity:         lot.current_quantity,
            moisture_content: lot.moisture_content ?? 14,
        }]);
    };

    const removeLot = (palayLotId) => {
        setSelectedLots(prev => prev.filter(l => l.palay_lot_id !== palayLotId));
    };

    const updateLotField = (palayLotId, field, value) => {
        setSelectedLots(prev => prev.map(l =>
            l.palay_lot_id === palayLotId ? { ...l, [field]: value } : l
        ));
    };

    const totalQty = selectedLots.reduce((s, l) => s + (l.quantity || 0), 0);
    const availableNotSelected = availableLots.filter(
        lot => !selectedLots.find(s => s.palay_lot_id === lot.id)
    );

    const onFinish = async (values) => {
        if (selectedLots.length === 0) {
            message.error('Add at least one dried palay lot');
            return;
        }
        setSubmitting(true);
        try {
            await client.post('/transactions/palay-hauling', {
                branch_id:               values.branch_id,
                source_location_id:      values.source_location_id,
                destination_location_id: values.destination_location_id,
                vehicle_id:              values.vehicle_id,
                driver_name:             values.driver_name,
                driver_contact:          values.driver_contact,
                scheduled_date:          values.scheduled_date?.format('YYYY-MM-DD'),
                remarks:                 values.remarks,
                lots: selectedLots.map(l => ({
                    palay_lot_id:     l.palay_lot_id,
                    quantity:         l.quantity,
                    moisture_content: l.moisture_content,
                })),
            });
            message.success('Palay hauling order created');
            navigate('/palay-hauling');
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to create hauling order');
        } finally {
            setSubmitting(false);
        }
    };

    const lotColumns = [
        { title: 'Lot #', dataIndex: 'lot_number', width: 120, render: (v) => <Text strong>{v}</Text> },
        { title: 'Supplier', dataIndex: 'supplier', ellipsis: true },
        { title: 'Purchase Ticket', dataIndex: 'purchase_ticket' },
        { title: 'Dried Via', dataIndex: 'drying_number', render: (v) => <Tag color="gold">{v}</Tag> },
        {
            title: 'Quantity (kg)',
            dataIndex: 'quantity',
            width: 160,
            render: (v, r) => (
                <InputNumber
                    value={v}
                    min={0.001}
                    step={0.001}
                    style={{ width: 120 }}
                    addonAfter="kg"
                    onChange={(val) => updateLotField(r.palay_lot_id, 'quantity', val)}
                />
            ),
        },
        {
            title: 'MC (%)',
            dataIndex: 'moisture_content',
            width: 120,
            render: (v, r) => (
                <InputNumber
                    value={v}
                    min={0}
                    max={25}
                    step={0.1}
                    style={{ width: 90 }}
                    addonAfter="%"
                    onChange={(val) => updateLotField(r.palay_lot_id, 'moisture_content', val)}
                />
            ),
        },
        {
            title: '',
            align: 'right',
            width: 50,
            render: (_, r) => (
                <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeLot(r.palay_lot_id)} />
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/palay-hauling')} style={{ borderRadius: 8 }} />
                <div>
                    <Title level={3} style={{ margin: 0, fontWeight: 800 }}>New Palay Hauling Order</Title>
                    <Text type="secondary">Haul DRIED palay lots from storage to the milling facility.</Text>
                </div>
            </div>

            <Alert
                type="info"
                showIcon
                message="Only DRIED palay lots are eligible for hauling to mill. Make sure drying is completed before creating this order."
                style={{ marginBottom: 20, borderRadius: 8 }}
            />

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Card className="premium-card" style={{ marginBottom: 20 }}>
                    <Title level={5} style={{ marginTop: 0 }}>Hauling Details</Title>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}>
                                <Select placeholder="Select branch" onChange={(v) => { setBranchId(v); }} style={{ height: 40 }}>
                                    {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="source_location_id" label="From (Storage)" rules={[{ required: true }]}>
                                <Select placeholder="Origin location" showSearch optionFilterProp="children" style={{ height: 40 }}>
                                    {locations.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="destination_location_id" label="To (Mill)" rules={[{ required: true }]}>
                                <Select placeholder="Destination mill" showSearch optionFilterProp="children" style={{ height: 40 }}>
                                    {locations.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="scheduled_date" label="Scheduled Date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%', height: 40 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="vehicle_id" label="Vehicle">
                                <Select placeholder="Select vehicle" allowClear style={{ height: 40 }}>
                                    {vehicles.map(v => <Option key={v.id} value={v.id}>{v.plate_number} — {v.model}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="driver_name" label="Driver Name">
                                <Input placeholder="Driver full name" style={{ height: 40 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="driver_contact" label="Driver Contact">
                                <Input placeholder="Phone number" style={{ height: 40 }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="remarks" label="Remarks">
                                <Input placeholder="Optional notes" style={{ height: 40 }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Lot Selection */}
                <Card className="premium-card" style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <Title level={5} style={{ margin: 0 }}>Select Dried Palay Lots</Title>
                        {!branchId && <Text type="secondary">Select a branch first to see available dried lots.</Text>}
                    </div>

                    {branchId && (
                        lotsLoading ? <Spin /> : (
                            availableNotSelected.length === 0 ? (
                                <Alert type="warning" message="No DRIED lots available for this branch. Complete a drying order first." showIcon />
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {availableNotSelected.map(lot => (
                                        <Button
                                            key={lot.id}
                                            onClick={() => addLot(lot)}
                                            style={{ height: 'auto', padding: '8px 14px', borderRadius: 8, textAlign: 'left' }}
                                            icon={<PlusOutlined />}
                                        >
                                            <span>
                                                <Text strong>{lot.lot_number}</Text>
                                                <Tag color="green" style={{ marginLeft: 6, fontSize: 10 }}>DRIED</Tag>
                                                <br />
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    {fmt(lot.current_quantity)} kg · MC: {lot.moisture_content ?? '14'}% · {lot.purchase?.partner?.name}
                                                </Text>
                                            </span>
                                        </Button>
                                    ))}
                                </div>
                            )
                        )
                    )}
                </Card>

                {selectedLots.length > 0 && (
                    <Card className="premium-card" style={{ marginBottom: 20 }}>
                        <Title level={5} style={{ margin: '0 0 16px 0' }}>Lots for Hauling ({selectedLots.length})</Title>
                        <Table
                            dataSource={selectedLots}
                            columns={lotColumns}
                            rowKey="palay_lot_id"
                            pagination={false}
                            size="middle"
                            scroll={{ x: 750 }}
                        />
                        <Divider />
                        <Row gutter={24}>
                            <Col xs={12} sm={6}>
                                <Statistic title="Total Lots" value={selectedLots.length} />
                            </Col>
                            <Col xs={12} sm={6}>
                                <Statistic title="Total Quantity" value={totalQty} suffix="kg" precision={2} valueStyle={{ color: '#2563eb', fontWeight: 700 }} />
                            </Col>
                        </Row>
                    </Card>
                )}

                <Space>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitting}
                        icon={<TruckOutlined />}
                        disabled={selectedLots.length === 0}
                        style={{ height: 44, borderRadius: 8, fontWeight: 600, paddingInline: 24 }}
                    >
                        Create Hauling Order
                    </Button>
                    <Button onClick={() => navigate('/palay-hauling')} style={{ height: 44, borderRadius: 8 }}>Cancel</Button>
                </Space>
            </Form>
        </div>
    );
};

export default PalayHaulingForm;
